import { SNAPSHOT_TOP_INTERESTS_PER_DIMENSION } from '@blisspointmedia/bpm-types/dist/YoutubeCreatorSets';
import {
  CreatorSetAudienceAttribute,
  YouTubeCreatorSetMember
} from '@blisspointmedia/bpm-types/dist/pgTables/YoutubeCreatorSetMember';
import { searchItems } from '@src/utils/search';
import { ViewerDetails } from '../Discover/creator-discovery-constants';
import { AGE_KEYS } from '../youtube-planning-constants';
import { attributeId, percent } from './creator-set-attributes-utils';
import {
  ALL_INTEREST_TYPES,
  AudienceDimension,
  BROAD_SHARE_MIN,
  DEMOGRAPHIC_DIMENSIONS,
  DemographicDimension,
  EXPORT_PARTNERSHIP_COLUMNS,
  INTEREST_DIMENSIONS,
  INTEREST_TYPE_LABELS,
  InterestDimension,
  InterestSort,
  InterestSortValue,
  InterestTypeFilter,
  LEAD_MIN,
  STANDOUT_LIMIT
} from './creator-sets-constants';
import { parseCount, sharesViewerData } from './creator-sets-utils';

// One creator's value for one interest. `share` is set when the interest is in the creator's
// snapshot. Otherwise `ceiling` is the lowest share the snapshot kept for that creator and type:
// the snapshot keeps each type's top interests, so the missing share is below it; unset when the
// snapshot has no interests of that type.
export interface InterestCell {
  share?: number;
  ceiling?: number;
}

export interface InterestRow {
  id: string;
  label: string;
  dimension: InterestDimension;
  // By channel id, for every compared creator.
  cells: Record<string, InterestCell>;
  // The highest share any compared creator has.
  maxShare: number;
  // How far the top creator is above the next one, taking a missing share at its ceiling (so the
  // real lead is at least this). 0 with fewer than two compared creators, or when the top creator's
  // share is only a ceiling.
  lead: number;
  // The channel id of the creator who leads by LEAD_MIN or more.
  leaderId?: string;
  isBroad: boolean;
}

interface InterestRowFilters {
  type: InterestTypeFilter;
  query: string;
  hideBroad: boolean;
  sort: InterestSortValue;
  // When set, sorts by this creator's share instead of `sort`.
  sortChannelId?: string;
}

interface ShareRow<D extends string> {
  dimension: D;
  id: string;
  label: string;
  // Viewer share by channel id. A creator without the attribute has no entry.
  shares: Record<string, number>;
}

export type DemographicRow = ShareRow<DemographicDimension>;

export interface CreatorSummary {
  channelId: string;
  name: string;
  thumbnailUrl: string;
  subscribers?: number;
  totalViews?: number;
  sharesViewerData: boolean;
  partnerships: string[];
  isActiveShortsCreator: boolean;
  // The median views of the stored top videos.
  medianTopVideoViews?: number;
  // The newest stored top video's publish date.
  newestTopVideo?: string;
  // Labels of the interests this creator leads.
  standsOutFor: string[];
}

const isInterestDimension = (dimension: string): dimension is InterestDimension =>
  INTEREST_DIMENSIONS.some(value => value === dimension);

const isDemographicDimension = (dimension: string): dimension is DemographicDimension =>
  DEMOGRAPHIC_DIMENSIONS.some(value => value === dimension);

const rowKey = (dimension: string, id: string): string => `${dimension}|${id}`;

// The creators who share viewer data, in set order.
export const comparedMembers = (members: YouTubeCreatorSetMember[]): YouTubeCreatorSetMember[] =>
  members.filter(sharesViewerData);

// One row per attribute of the chosen dimensions that any compared creator has a share for.
const collectShares = <D extends string>(
  compared: YouTubeCreatorSetMember[],
  isDimension: (dimension: string) => dimension is D
): ShareRow<D>[] => {
  const rows = new Map<string, ShareRow<D>>();
  compared.forEach(({ channel_id, insights }) => {
    insights.channel_audience_attributes.forEach(attribute => {
      const { dimension, displayName, viewerShare } = attribute;
      if (!isDimension(dimension) || viewerShare === undefined) return;
      const id = attributeId(attribute);
      const key = rowKey(dimension, id);
      const row = rows.get(key) ?? { dimension, id, label: displayName, shares: {} };
      row.shares[channel_id] = viewerShare;
      rows.set(key, row);
    });
  });
  return [...rows.values()];
};

const lowestShareByDimension = (attributes: CreatorSetAudienceAttribute[]) => {
  const lowest = new Map<string, number>();
  attributes.forEach(({ dimension, viewerShare }) => {
    if (viewerShare === undefined) return;
    lowest.set(dimension, Math.min(lowest.get(dimension) ?? Infinity, viewerShare));
  });
  return lowest;
};

const scoreRow = (
  row: Omit<InterestRow, 'maxShare' | 'lead' | 'leaderId' | 'isBroad'>,
  channelIds: string[]
): InterestRow => {
  const values = channelIds
    .map(channelId => {
      const { share, ceiling } = row.cells[channelId];
      return { channelId, share, upper: share ?? ceiling ?? 0 };
    })
    .sort((a, b) => b.upper - a.upper);
  const [top, next] = values;
  const maxShare = Math.max(...values.map(({ share }) => share ?? 0));
  // Whole points, so float noise (0.65 - 0.46 > 0.71 - 0.52) can't break ties.
  const lead =
    top.share !== undefined && next ? Math.round((top.share - next.upper) * 100) / 100 : 0;
  return {
    ...row,
    maxShare,
    lead,
    leaderId: lead >= LEAD_MIN ? top.channelId : undefined,
    isBroad: values.length > 1 && values.every(({ share }) => (share ?? 0) >= BROAD_SHARE_MIN)
  };
};

// Every topic, affinity and in-market interest in any compared creator's snapshot, one row each,
// with each compared creator's share side by side.
export const toInterestRows = (compared: YouTubeCreatorSetMember[]): InterestRow[] => {
  const channelIds = compared.map(({ channel_id }) => channel_id);
  const lowestByChannel = new Map(
    compared.map(({ channel_id, insights }) => [
      channel_id,
      lowestShareByDimension(insights.channel_audience_attributes)
    ])
  );
  return collectShares(compared, isInterestDimension).map(({ dimension, id, label, shares }) =>
    scoreRow(
      {
        id,
        label,
        dimension,
        cells: Object.fromEntries(
          channelIds.map(channelId => [
            channelId,
            channelId in shares
              ? { share: shares[channelId] }
              : { ceiling: lowestByChannel.get(channelId)?.get(dimension) }
          ])
        )
      },
      channelIds
    )
  );
};

const byDistinctive = (a: InterestRow, b: InterestRow) =>
  b.lead - a.lead || b.maxShare - a.maxShare || a.label.localeCompare(b.label);
const byShare = (a: InterestRow, b: InterestRow) =>
  b.maxShare - a.maxShare || b.lead - a.lead || a.label.localeCompare(b.label);
// Interests missing from the creator's snapshot go last.
const byCreatorShare = (channelId: string) => (a: InterestRow, b: InterestRow) =>
  (b.cells[channelId].share ?? -1) - (a.cells[channelId].share ?? -1) || byDistinctive(a, b);

const interestComparator = (sort: InterestSortValue, sortChannelId?: string) => {
  if (sortChannelId) return byCreatorShare(sortChannelId);
  return sort === InterestSort.DISTINCTIVE ? byDistinctive : byShare;
};

// The rows to show for the chosen type, search text and broad-interest setting, in the chosen order.
export const filterInterestRows = (
  rows: InterestRow[],
  { type, query, hideBroad, sort, sortChannelId }: InterestRowFilters
): InterestRow[] => {
  const typed = rows.filter(row => type === ALL_INTEREST_TYPES || row.dimension === type);
  return searchItems(typed, query, { keys: ['label'], rank: false })
    .filter(row => !hideBroad || !row.isBroad)
    .sort(interestComparator(sort, sortChannelId));
};

// The hover text for an interest that isn't in a creator's snapshot.
export const missingInterestTooltip = (dimension: InterestDimension, ceiling?: number): string => {
  const type = INTEREST_TYPE_LABELS[dimension].toLowerCase();
  if (ceiling === undefined) return `No ${type} interests in this creator’s snapshot`;
  return `Not in this creator’s top ${SNAPSHOT_TOP_INTERESTS_PER_DIMENSION} ${type} interests, so under ${percent(ceiling)}`;
};

// What sets one creator's audience apart: the interests they lead, biggest lead first.
export const standoutInterests = (rows: InterestRow[], channelId: string): string[] =>
  rows
    .filter(row => row.leaderId === channelId)
    .sort(byDistinctive)
    .slice(0, STANDOUT_LIMIT)
    .map(({ label }) => label);

const ageRank = (id: string): number => {
  const index = AGE_KEYS.findIndex(key => key === id);
  return index === -1 ? AGE_KEYS.length : index;
};

const meanShare = ({ shares }: DemographicRow, count: number): number =>
  Object.values(shares).reduce((sum, share) => sum + share, 0) / count;

// Gender, age, device and country shares per compared creator. Age keeps the AGE_KEYS order;
// the others are largest first by the mean share across the compared creators.
export const toDemographicRows = (compared: YouTubeCreatorSetMember[]): DemographicRow[] => {
  const rows = collectShares(compared, isDemographicDimension);
  return DEMOGRAPHIC_DIMENSIONS.flatMap(dimension =>
    rows
      .filter(row => row.dimension === dimension)
      .sort((a, b) =>
        dimension === AudienceDimension.AGE_RANGE
          ? ageRank(a.id) - ageRank(b.id)
          : meanShare(b, compared.length) - meanShare(a, compared.length)
      )
  );
};

const median = (values: number[]): number | undefined => {
  if (values.length === 0) return undefined;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0 ? (sorted[middle - 1] + sorted[middle]) / 2 : sorted[middle];
};

const newestDate = (dates: string[]): string | undefined =>
  dates
    .filter(date => Number.isFinite(Date.parse(date)))
    .sort((a, b) => Date.parse(b) - Date.parse(a))[0];

// One card's worth of facts per creator, in set order.
export const toCreatorSummaries = (
  members: YouTubeCreatorSetMember[],
  interestRows: InterestRow[]
): CreatorSummary[] =>
  members.map(member => {
    const { channel_id, insights } = member;
    const videos = insights.top_videos ?? [];
    return {
      channelId: channel_id,
      name: insights.display_name,
      thumbnailUrl: insights.thumbnail_url,
      subscribers: parseCount(insights.subscriber_count),
      totalViews: parseCount(insights.views_count),
      sharesViewerData: sharesViewerData(member),
      partnerships: EXPORT_PARTNERSHIP_COLUMNS.filter(({ type }) =>
        (insights.partnership_opportunities ?? []).includes(type)
      ).map(({ label }) => label),
      isActiveShortsCreator: insights.is_active_shorts_creator,
      medianTopVideoViews: median(
        videos.map(video => parseCount(video.views_count)).filter(views => views !== undefined)
      ),
      newestTopVideo: newestDate(videos.map(video => video.publish_date)),
      standsOutFor: standoutInterests(interestRows, channel_id)
    };
  });

// The tint class for a share cell. A share under 40% gets none.
export const shareLevelClass = (share: number): string | undefined => {
  if (share >= 0.9) return 'level-4';
  if (share >= 0.75) return 'level-3';
  if (share >= 0.6) return 'level-2';
  if (share >= 0.4) return 'level-1';
  return undefined;
};

// The "Audience stands out for" line of a creator's summary card.
export const standoutText = (
  { sharesViewerData: hasViewerData, standsOutFor }: CreatorSummary,
  canCompare: boolean
): string => {
  if (!hasViewerData) return ViewerDetails.NOT_SHARED;
  if (!canCompare) return 'Needs a second creator with viewer data';
  return standsOutFor.length > 0 ? standsOutFor.join(', ') : 'No interest leads the others';
};
